import { Box, CircularProgress, Fab, Grid, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableRow, TextField, Theme, Typography } from '@mui/material'
import { Field, Form, Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { IVehiclePriceGroup } from '../../../../../reducers/vehiclePriceGroups/types'
import { IAppState } from '../../../../../store'
import { DATE_TYPE, formatGraphQLErrorMessage, toCurrency } from '../../../../common/utils'
import { ApolloError, useLazyQuery } from '@apollo/client'
import { GET_BRANCH } from '../../../../../graphql/organisation/getBranch'
import { GET_SERVICE_LOCATIONS_IN_ORG } from '../../../../../graphql/vehicleMovement/queries'
import { GET_AVAILABLE_VEHICLE_GROUPS } from '../../../../../graphql/bookings/getAvailableVehicleGroups'
import { useSnackBar } from '../../../../common/SnackBarContext/SnackBarContext'
import { SnackBarVariant } from '../../../../common/SnackbarWrapper/SnackbarWrapper'
import { GET_BUSINESS_CUSTOMERS } from '../../../../../graphql/businessCustomers/businessCustomer'
import { GET_EXTERNAL_AVAILABLE_VEHICLE_GROUPS } from '../../../../../graphql/bookings/getExternalAvailableVehicleGroups'
import { IVehicle } from '../../../../../reducers/fleet/types'
import { BookingStatus, IBooking, IBookingVehicleGroup } from '../../../../../reducers/bookings/types'
import { createStyles, makeStyles } from '@mui/styles'
import { getLocalizedBookingSyntex, getLocalizedDateFormat } from '../../../../../utils/localized.syntex'
import { IReplaceVehicleWithDragArgs } from '../utils'
import { ValueType } from '../../../../../reducers/priceRule/types'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    table: {
    },
    tableCell: {
      border: "1px solid rgba(224, 224, 224, 1)"
    },
    dialogContent: {
      paddingTop: 10
    }
  })
)
interface IDropEvent {
  startDate: string;
  endDate: string;
  bookingId: string;
  bookingScheduleId: string;
  vehicleGroupId: string;
  previousVehicle: string;
  currentVehicle: string;
  rateTypeName?: string;
  rateTypeDuration?: number;
  vehicleGropups?: IVehiclePriceGroup[];
}

interface IProps {
  dropEventData?: IDropEvent;
  resource?: IVehicle;
  booking: IBooking;
  handleClose(type?: boolean): void;
  shiftBookingSchedule(values: IDropEvent): void
  replaceVehicleWithDragAndDrop(args: IReplaceVehicleWithDragArgs, reason: string): void
  replaceDragDropVehicleLoading: boolean
}

const initialValues: IDropEvent = {
  rateTypeDuration: 0,
  rateTypeName: "",
  startDate: "",
  endDate: "",
  bookingId: "",
  bookingScheduleId: "",
  vehicleGroupId: "",
  previousVehicle: "",
  currentVehicle: "",
  vehicleGropups: []
}
export const DragAndDropBookingEvent: React.FC<IProps> = (props) => {

  const snackbar = useSnackBar();
  const classes = useStyles();

  const { dropEventData, handleClose, replaceVehicleWithDragAndDrop, resource, booking, replaceDragDropVehicleLoading } = props

  const userState = useSelector((state: IAppState) => state.userReducer);
  const { locale, currency } = userState.currentOrganisation;
  const { country } = userState.organisation.address

  const [values, setValues] = useState<IDropEvent>(initialValues)
  const [vehicleGroups, setVehicleGroups] = useState<IVehiclePriceGroup[]>([])
  const [selectedVehicleGroup, setSelectedVehicleGroup] = useState<IVehiclePriceGroup>()
  const [selectedVehicle, setSelectedVehicle] = useState<IVehicle>()
  const [vehicles, setVehicles] = useState<IVehicle[]>()

  const [loadVehicleGroups,
    { loading: vehicleGroupsLoading,
      data: availableVehicleGroupsData }
  ] = useLazyQuery(
    GET_AVAILABLE_VEHICLE_GROUPS, {
    fetchPolicy: "network-only",
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  });

  const [
    loadExternalVehicleGroups,
    { loading: externalVehicleGroupsLoading, data: externalAvailableVehicleGroupsData }
  ] = useLazyQuery(GET_EXTERNAL_AVAILABLE_VEHICLE_GROUPS, {
    fetchPolicy: "no-cache",
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  });

  useEffect(() => {
    if (resource && resource.id) {
      setValues({
        ...values,
        currentVehicle: resource.id,
        bookingId: booking.id,
        bookingScheduleId: booking.currentBookingSchedules[0].id,
        endDate: dropEventData?.endDate || "",
        previousVehicle: dropEventData?.previousVehicle || "",
        rateTypeDuration: booking?.rateTypeDuration,
        rateTypeName: dropEventData?.rateTypeName,
        startDate: dropEventData?.startDate || "",
      })
    }
  }, [resource])

  useEffect(() => {
    if (booking) {
      loadVehicleGroups({
        variables: {
          args: {
            endDate: dropEventData?.endDate,
            startDate: dropEventData?.startDate,
            vehicleId: "",
            rateTypeDuration: booking.rateTypeDuration,
            businessCustomer: booking.businessCustomer && booking.businessCustomer.id ? booking.businessCustomer.id : "",
            longTerm: false,
            serviceLocation: booking.pickupServiceLocation.id
          }
        }
      })
      loadExternalVehicleGroups({
        variables: {
          args: {
            endDate: dropEventData?.endDate,
            startDate: dropEventData?.startDate,
            vehicleId: "",
            rateTypeDuration: booking.rateTypeDuration,
            businessCustomer: booking.businessCustomer && booking.businessCustomer.id ? booking.businessCustomer.id : "",
            longTerm: false,
            serviceLocation: booking.pickupServiceLocation.id
          }
        }
      })
    }
  }, [booking])

  useEffect(() => {
    const vehicleGroupsData = []
    const locationSurcharges = []
    if (resource && resource.branchId === userState.currentBranch.id) {
      if (availableVehicleGroupsData && availableVehicleGroupsData.availableVehicleGroups) {
        vehicleGroupsData.push(...availableVehicleGroupsData.availableVehicleGroups.vehicleGroups)
        locationSurcharges.push(...availableVehicleGroupsData.availableVehicleGroups.locationSurcharges)
      }
    } else {
      if (externalAvailableVehicleGroupsData && externalAvailableVehicleGroupsData.externalAvailableVehicleGroups) {
        vehicleGroupsData.push(...externalAvailableVehicleGroupsData.externalAvailableVehicleGroups.vehicleGroups)
        locationSurcharges.push(...externalAvailableVehicleGroupsData.externalAvailableVehicleGroups.locationSurcharges)
      }
    }
    if (vehicleGroupsData?.length > 0) {
      const filteredGroups = vehicleGroupsData.filter(group =>
        group.vehicles.some((vehicle: IVehicle) => vehicle.id === dropEventData?.currentVehicle)
      );

      const selectedGroup = filteredGroups.length ? filteredGroups[0] : []

      if (selectedGroup && selectedGroup.id) {
        const selectedVehicle = selectedGroup.vehicles.find((v: IVehicle) => v.id === dropEventData?.currentVehicle)
        setSelectedVehicleGroup(selectedGroup)
        setVehicleGroups(filteredGroups)
        setSelectedVehicle(selectedVehicle)
        setVehicles(selectedGroup.vehicles)
      }
    }
  }, [availableVehicleGroupsData, externalAvailableVehicleGroupsData])

  if (vehicleGroupsLoading || externalVehicleGroupsLoading) {
    return (
      <Grid>
        <CircularProgress />
      </Grid>
    )
  }

  return (
    <Grid container item xs={12} style={{ margin: "15px 0px" }}>
      <Formik
        enableReinitialize
        initialValues={values}
        onSubmit={(values) => {
          handleClose()
        }}
      >
        {() => (
          <Form style={{ flexGrow: 1 }}>
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12}>
                <TableContainer component={Paper}>
                  <Table aria-label="simple table" size={"small"} className={classes.table}>
                    <TableBody>
                      <TableRow>
                        <TableCell className={classes.tableCell}><Typography variant="h4">{`${getLocalizedBookingSyntex(country)} Reference`}</Typography></TableCell>
                        <TableCell className={classes.tableCell}><Typography variant="body1">{booking?.referenceNumber}</Typography></TableCell>
                        <TableCell className={classes.tableCell}><Typography variant="h4">{"Pick-up Date & Time"}</Typography></TableCell>
                        {/* TODO: Add locale to formal date */}
                        <TableCell className={classes.tableCell}><Typography variant="body1">{booking?.pickupDateTime && getLocalizedDateFormat(country, booking.pickupDateTime, DATE_TYPE.EXPANDED)}</Typography></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCell}><Typography variant="h4">{booking?.tba ? "Renewal Date & Time" : "Drop-off Date & Time"}</Typography></TableCell>
                        <TableCell className={classes.tableCell}><Typography variant="body1">{booking?.dropoffDateTime && getLocalizedDateFormat(country, booking.dropoffDateTime, DATE_TYPE.EXPANDED)}</Typography></TableCell>
                        <TableCell className={classes.tableCell}><Typography variant="h4">
                          {
                            (booking?.status === BookingStatus.CONFIRMED && !booking.dropoffOtherLocation) ? "Pick-up Location" :
                              (booking?.status === BookingStatus.CONFIRMED && booking.dropoffOtherLocation) ? "Delivery Address" :
                                (booking?.status === BookingStatus.IN_PROGRESS && !booking.dropoffOtherLocation) ? "Drop-off Location" :
                                  (booking?.status === BookingStatus.IN_PROGRESS && booking.dropoffOtherLocation) && "Collection Address"
                          }
                        </Typography></TableCell>
                        <TableCell className={classes.tableCell} style={{ maxWidth: 0 }}>
                          <Typography variant="body1">
                            {
                              (booking?.status === BookingStatus.CONFIRMED && !booking.pickupOtherLocation) ? booking?.pickupServiceLocation.name :
                                (booking?.status === BookingStatus.CONFIRMED && booking.pickupOtherLocation) ? booking.pickupOtherLocation.fullAddress :
                                  (booking?.status === BookingStatus.IN_PROGRESS && !booking.dropoffOtherLocation) ? booking?.dropoffServiceLocation.name :
                                    (booking?.status === BookingStatus.IN_PROGRESS && booking.dropoffOtherLocation) && booking.dropoffOtherLocation.fullAddress
                            }
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className={classes.tableCell}><Typography variant="h4">{`${getLocalizedBookingSyntex(country)} Branch Name`}</Typography></TableCell>
                        <TableCell>
                          <Typography variant="body1">
                            {
                              userState.currentOrganisation.branches.map(branch => {
                                if (branch.id === userState.currentBranch.id && booking.branchId === userState.currentBranch.id) {
                                  return "own";
                                }
                                if (booking.branchId === branch.id && booking.branchId !== userState.currentBranch.id) {
                                  return branch.name
                                }
                              })
                            }
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12}><Typography variant="h3">Select vehicle group and vehicle details</Typography></Grid>
              <Grid container item xs={6}>
                {
                  vehicleGroups && vehicleGroups.length ? (
                    <Field
                      component={TextField}
                      name={"vehicleGroup"}
                      fullWidth
                      required
                      type="text"
                      select
                      label="Vehicle Group"
                      inputProps={{
                        onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                          const selectedVehicleGroup = vehicleGroups.find((vg) => {
                            return vg.id === event.target.value
                          })
                          if (selectedVehicleGroup && dropEventData) {
                            setSelectedVehicleGroup(selectedVehicleGroup)
                            const selectedVehicle = selectedVehicleGroup.vehicles.find(v => v.id === dropEventData.currentVehicle)
                            setSelectedVehicle(selectedVehicle)
                          }
                        },
                        value: selectedVehicleGroup?.id
                      }}
                      InputLabelProps={{
                        shrink: true
                      }}
                    >
                      {vehicleGroups.map(
                        (vehicleGroup, index: number) => {
                          const basePrice = vehicleGroup.basePrices.find((bs) => bs.rateTypeDuration === booking.rateTypeDuration)
                          if (basePrice) {
                            return (
                              <MenuItem key={index} value={vehicleGroup.id}>
                                {vehicleGroup.name}
                              </MenuItem>)
                          }
                        }
                      )}
                    </Field>
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant='h5' color={"error"}>No vehicle group found for the selected vehicle. Please try with some other vehicle.</Typography>
                    </Grid>
                  )
                }
              </Grid>
              <Grid item xs={6}>
                {
                  vehicles && vehicles.length && (
                    <Field
                      component={TextField}
                      name={"vehicle"}
                      fullWidth
                      required
                      type="text"
                      select
                      label="Select Vehicle"
                      inputProps={{
                        onChange: (event: React.ChangeEvent<HTMLInputElement>) => {

                        },
                        value: selectedVehicle?.id
                      }}
                      disabled
                      InputLabelProps={{
                        shrink: true
                      }}
                    >
                      {
                        vehicles.map(
                          (vehicle, index: number) => {
                            return (
                              <MenuItem key={index} value={vehicle.id}>
                                {vehicle.licencePlate}
                              </MenuItem>
                            )
                          })
                      }
                    </Field>
                  )
                }
              </Grid>
              <Grid container item xs={12}>
                <Fab
                  variant={"extended"}
                  size={"medium"}
                  onClick={() => {
                    if (selectedVehicleGroup && selectedVehicleGroup.id) {
                      const basePrice = selectedVehicleGroup?.basePrices.find((bp) => bp.rateTypeDuration === booking?.rateTypeDuration)
                      if (basePrice) {
                        const args: IReplaceVehicleWithDragArgs = {
                          bookingId: booking.id || "",
                          bookingScheduleId: dropEventData?.bookingScheduleId || "",
                          endDate: dropEventData?.endDate || "",
                          previousVehicleGroupId: dropEventData?.vehicleGroupId || "",
                          previousVehicleId: dropEventData?.previousVehicle || "",
                          reason: "DRAG_DROP",
                          startDate: dropEventData?.startDate || "",
                          vehicleGroup: {
                            baseRate: basePrice.applicableAmount ? basePrice.applicableAmount : basePrice.rate,
                            count: 1,
                            deposit: selectedVehicleGroup.deposit,
                            mileageLimit: basePrice.mileageLimit,
                            pricePerExtraMile: basePrice.pricePerExtraMile,
                            priceRule: basePrice.priceRule || "",
                            priceRuleAmount: basePrice.priceRuleAmount || 0,
                            priceRuleName: basePrice.priceRuleName || "",
                            priceRuleOperator: basePrice.priceRuleOperator,
                            priceRuleTriggerType: basePrice.priceRuleTriggerType || "",
                            priceRuleValueType: basePrice.priceRuleValueType,
                            unlimitedMileage: basePrice.unlimitedMileage || false,
                            vehicleGroup: selectedVehicleGroup.id || "",
                            vehicleIds: [selectedVehicle?.id || ""],
                            name: selectedVehicleGroup.name
                          },
                        }
                        replaceVehicleWithDragAndDrop(args, "CONFIRMED")
                      }
                    }

                  }}
                  disabled={!selectedVehicle?.id && !selectedVehicleGroup?.id}
                >
                  {replaceDragDropVehicleLoading ? <CircularProgress /> : "Confirm"}
                </Fab>
                <Box pl={2}></Box>
                <Fab
                  variant={"extended"}
                  size={"medium"}
                  className={"blackBackButton"}
                  onClick={() => {
                    const args: IReplaceVehicleWithDragArgs = {
                      bookingId: booking.id || "",
                      bookingScheduleId: dropEventData?.bookingScheduleId || "",
                      endDate: dropEventData?.endDate || "",
                      previousVehicleGroupId: dropEventData?.vehicleGroupId || "",
                      previousVehicleId: dropEventData?.previousVehicle || "",
                      reason: "DRAG_DROP",
                      startDate: dropEventData?.startDate || "",
                      vehicleGroup: {
                        baseRate: 0,
                        count: 1,
                        deposit: 0,
                        mileageLimit: 0,
                        pricePerExtraMile: 0,
                        priceRule: "",
                        priceRuleAmount: 0,
                        priceRuleName: "",
                        // priceRuleOperator: ,
                        priceRuleTriggerType: "",
                        priceRuleValueType: ValueType.FIXED,
                        unlimitedMileage: false,
                        vehicleGroup: "",
                        vehicleIds: [],
                        name: ""
                      },
                    }
                    replaceVehicleWithDragAndDrop(args, "CANCEL")
                  }}
                >
                  Cancel
                </Fab>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Grid>
  )
}
